import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { weekdays } from 'moment'
import { catchError, map } from 'rxjs/operators'
import { UUID } from '../../app/common/components/types/uuid'
import { GroupBy } from '../../app/common/model/hierarchy-menu.model'
import { GroupbyIconService } from '../../app/common/services/groupby-icon.service'
import { PpwLocalStorageService } from '../../app/common/services/ppwlocalstorage.service'
import { notUndefined } from '../../app/common/utils/conditional-assert'
import { setTotals, TimeCardContainer } from '../../app/ui/timecards/model/timecard-container.model'
import { Employee } from '../models/employee.model'
import { allMonths, Month, Months } from '../models/months.enum'
import { TimeCard, TimeCardDTO, TimeCardPaginatedList, TimeCardSearchCriteria } from '../models/timecard.model'
import { BaseService } from './base.service'
import { EmployeeService } from './employee.service'

@Injectable({
    providedIn: 'root'
})
export class TimeCardsService extends BaseService {
    private allEmployees?: Array<Employee>

    private static sortTimecards(items: Array<TimeCardContainer>): Array<TimeCardContainer> {
        return items.sort((a, b) => {
            const result = notUndefined(a.name).localeCompare(notUndefined(b.name))

            return result
        })
    }

    private static sortByNumber(items: Array<TimeCardContainer>): Array<TimeCardContainer> {
        return items.sort((a, b) => {
            const numberA = Number(notUndefined(a.name).split(' ')[0])
            const numberB = Number(notUndefined(b.name).split(' ')[0])
            if (numberA < numberB) {
                return -1
            }
            if (numberA > numberB) {
                return 1
            }

            return 0
        })
    }

    private static sortByIssueKey(items: Array<TimeCardContainer>): Array<TimeCardContainer> {
        return items.sort((a, b) => {
            if (!a.issueKey) {
                return -1
            } else if (!b.issueKey) {
                return 1
            }
            const numberA = Number(a.issueKey.split('-')[1])
            const numberB = Number(b.issueKey.split('-')[1])
            if (numberA < numberB) {
                return -1
            }
            if (numberA > numberB) {
                return 1
            }

            return 0
        })
    }
    get uri(): string {
        return `${this.baseUri}/TimeCards`
    }

    constructor(
        private readonly ppwLocalStorageService: PpwLocalStorageService,
        private readonly employeeService: EmployeeService,
        private readonly groupByIconService: GroupbyIconService,
        readonly http: HttpClient
    ) {
        super(http)
    }

    async getTimeCards(timeCardSearchCriteria: TimeCardSearchCriteria): Promise<TimeCardPaginatedList> {
        const preferences = this.ppwLocalStorageService.getStoredData(
            this.ppwLocalStorageService.STORAGE_KEY_PROFILE_PREFERENCES
        )
        timeCardSearchCriteria.includeArchived = preferences && preferences.showArchivedData
        timeCardSearchCriteria.includeArchived = true // Marc S.: never hide logged hours in timecards nor timecards

        return this.$http
            .get<TimeCardPaginatedList>(this.uri, { params: timeCardSearchCriteria.httpParams(), observe: 'response' })
            .pipe(
                map((response) => TimeCardPaginatedList.fromJSON(this.preCheckResponseData(response))),
                catchError((err) => this.handleError(err))
            )
            .toPromise()
    }

    async saveTimeCard(timeCard: TimeCard): Promise<TimeCard> {
        const uri = timeCard.id ? `${this.uri}/${timeCard.id}` : this.uri

        if (timeCard.id) {
            return this.$http
                .put<TimeCard>(uri, timeCard, { observe: 'response' })
                .pipe(
                    map((response) => TimeCard.fromJSON(this.preCheckResponseData(response) as TimeCardDTO)),
                    catchError((err) => this.handleError(err))
                )
                .toPromise()
        }

        return this.$http
            .post<TimeCard>(uri, timeCard, { observe: 'response' })
            .pipe(
                map((response) => TimeCard.fromJSON(this.preCheckResponseData(response) as TimeCardDTO)),
                catchError((err) => this.handleError(err))
            )
            .toPromise()
    }

    async deleteTimeCard(id: number): Promise<boolean> {
        const uri = `${this.uri}/${id}`

        return this.$http
            .delete<TimeCard>(uri, { observe: 'response' })
            .pipe(
                map(() => true),
                catchError(async (err) => this.handleError(err))
            )
            .toPromise()
    }

    async groupTimeCards(
        timeCards: Array<TimeCardContainer>,
        groupByArray: Array<GroupBy>
    ): Promise<Array<TimeCardContainer>> {
        this.allEmployees = (await this.employeeService.getAllEmployees()).items
        const result: Array<TimeCardContainer> = this.groupTimeCardContainer(timeCards, 'year')
        if (groupByArray.length > 1) {
            this.groupByAllChildren(result, groupByArray)
        }

        return result
    }

    groupByAllChildren(items: Array<TimeCardContainer>, groupBy: Array<GroupBy>): void {
        items.forEach((timesheetContainer: TimeCardContainer) => {
            timesheetContainer.children = this.groupTimeCardContainer(
                notUndefined(timesheetContainer.children),
                groupBy[0]
            )
            if (groupBy.length > 1 && timesheetContainer.children.length > 0) {
                this.groupByAllChildren(timesheetContainer.children, groupBy.slice(1))
            }
            if (groupBy.length === 1 && timesheetContainer.children) {
                timesheetContainer.children.forEach((item) => {
                    if (item.children) {
                        item.children = TimeCardsService.sortByIssueKey(item.children)
                        item.children.forEach((child) => {
                            child.parent = item
                        })
                    }
                })
            }
            timesheetContainer.children.forEach((item) => {
                item.parent = timesheetContainer
            })
        })
    }

    groupTimeCardContainer(items: Array<TimeCardContainer>, groupBy: GroupBy): Array<TimeCardContainer> {
        let result: Array<TimeCardContainer> = []
        const icon = this.groupByIconService.getIconByGroupBy(groupBy)
        if (groupBy === 'tag') {
            const tags: Set<string> = new Set()
            items.forEach((item) => {
                item.tags.forEach((tag) => {
                    tags.add(tag)
                })
            })

            tags.forEach((tag) => {
                const children = this.filterByTag(items, tag)

                const row = new TimeCardContainer(tag, icon, groupBy, children)
                result.push(row)
            })
            result = TimeCardsService.sortTimecards(result)
        }
        if (groupBy === 'month') {
            // don't reverse the original array
            const copyAllMonths = [...allMonths]
            copyAllMonths.reverse().forEach((month) => {
                const children = this.filterByMonth(items, month)
                if (children.length > 0) {
                    const row = new TimeCardContainer(month.toString(), icon, groupBy, children)
                    result.push(row)
                }
            })
        }

        if (groupBy === 'employee') {
            result = this.groupByEmployee(items, icon, groupBy)
            result = TimeCardsService.sortTimecards(result)
        }

        if (groupBy === 'timesheetProjectClient') {
            result = this.groupByTimesheetProjectClient(items, icon, groupBy)
            result = TimeCardsService.sortTimecards(result)
        }

        if (groupBy === 'client') {
            result = this.groupByClient(items, icon, groupBy)
            result = TimeCardsService.sortTimecards(result)
        }

        if (groupBy === 'timesheetProject') {
            result = this.groupByTimesheetProject(items, icon, groupBy)
            result = TimeCardsService.sortTimecards(result)
        }

        if (groupBy === 'day') {
            result = this.groupByDay(items, icon, groupBy)
            result = TimeCardsService.sortByNumber(result)
        }

        if (groupBy === 'year') {
            result = this.groupByYear(items, icon, groupBy)
            result = TimeCardsService.sortByNumber(result)
        }

        result.forEach(setTotals)

        return result
    }

    findGroupOfTimecard(
        timecard: TimeCardContainer,
        timecards: Array<TimeCardContainer>,
        groupByArray: Array<GroupBy>,
        previousResults: Array<TimeCardContainer> = []
    ): TimeCardContainer | undefined {
        let result
        const groupBy = notUndefined(groupByArray.shift())
        // NOTE: We don't search by tag here since we can't create / update / delete timecards there anyways.
        if (groupBy === 'year') {
            result = timecards.find((t) => t.name === `${timecard.date.getFullYear()}`)
        }
        if (groupBy === 'month') {
            result = timecards.find(
                (t) =>
                    t.name !== undefined &&
                    t.name.toUpperCase() === `${Months[timecard.date.getMonth()].toUpperCase()}` &&
                    this.hasParents(previousResults, t)
            )
        }
        if (groupBy === 'day') {
            result = timecards.find(
                (t) =>
                    t.name === `${timecard.date.getDate()} ${weekdays(timecard.date.getDay())}` &&
                    this.hasParents(previousResults, t)
            )
        }
        if (groupBy === 'employee') {
            result = timecards.find(
                (t) =>
                    (t.name === `${timecard.employeeFullName}` || t.name === `${timecard.fullName}`) &&
                    this.hasParents(previousResults, t)
            )
        }
        if (groupBy === 'timesheetProjectClient') {
            result = timecards.find(
                (t) =>
                    t.name === `${timecard.clientName} (${timecard.timesheetProjectName})` &&
                    this.hasParents(previousResults, t)
            )
        }
        if (groupBy === 'client') {
            result = timecards.find((t) => t.name === `${timecard.clientName}` && this.hasParents(previousResults, t))
        }
        if (groupBy === 'timesheetProject') {
            result = timecards.find(
                (t) => t.name === `${timecard.timesheetProjectName}` && this.hasParents(previousResults, t)
            )
        }
        if (groupByArray.length > 0 && result !== undefined) {
            previousResults.push(result)
            const deeperResult = this.findGroupOfTimecard(timecard, timecards, groupByArray, previousResults)
            result = deeperResult ? deeperResult : result
        }
        if (result === undefined) {
            groupByArray.reverse()
            groupByArray.push(groupBy)
            groupByArray.reverse()
        }

        return result
    }

    updateTotalsForAllParents(timecard: TimeCardContainer): void {
        if (timecard !== undefined) {
            setTotals(timecard)
            const parent = timecard.parent
            if (parent !== undefined) {
                const children = notUndefined(parent.children)
                const index = children.findIndex((t) => t.name === timecard.name)
                const cloneTimecard = { ...timecard, isExpanded: undefined } as unknown as TimeCardContainer
                children.splice(index, 1, cloneTimecard)
                this.updateTotalsForAllParents(parent)
            }
        }
    }

    hasParents(listOfParents: Array<TimeCardContainer>, timeCardContainer: TimeCardContainer): boolean {
        const parent = timeCardContainer.parent
        const result = listOfParents.some((p) => !parent || p.name === parent.name)

        if (result && parent) {
            return result && this.hasParents(listOfParents, parent)
        }

        return result
    }

    filterByTag(timecardsToFilter: Array<TimeCardContainer>, tag: string): Array<TimeCardContainer> {
        return timecardsToFilter.filter((t) => t.tags.has(tag))
    }

    filterByMonth(timecardsToFilter: Array<TimeCardContainer>, month: Month): Array<TimeCardContainer> {
        return timecardsToFilter.filter((t) => Months[t.date.getMonth()].toUpperCase() === month.toUpperCase())
    }

    groupByDay(
        timecardsToFilter: Array<TimeCardContainer>,
        icon: string,
        colorByGroupBy: string
    ): Array<TimeCardContainer> {
        return timecardsToFilter.reduce((arr, a) => {
            const id = arr.findIndex((timesheet) =>
                timesheet.children
                    ? timesheet.children.findIndex((child) => child.date.getDate() === a.date.getDate()) >= 0
                    : false
            )

            if (id >= 0) {
                notUndefined(arr[id].children).push(a)
            } else {
                arr.push(
                    new TimeCardContainer(`${a.date.getDate()} ${weekdays(a.date.getDay())}`, icon, colorByGroupBy, [a])
                )
            }

            return arr
        }, [] as Array<TimeCardContainer>)
    }

    groupByYear(
        timecardsToFilter: Array<TimeCardContainer>,
        icon: string,
        colorByGroupBy: string
    ): Array<TimeCardContainer> {
        return timecardsToFilter.reduce((arr, a) => {
            const id = arr.findIndex((timesheet) =>
                timesheet.children
                    ? timesheet.children.findIndex((child) => child.date.getFullYear() === a.date.getFullYear()) >= 0
                    : false
            )

            if (id >= 0) {
                notUndefined(arr[id].children).push(a)
            } else {
                arr.push(new TimeCardContainer(`${a.date.getFullYear()}`, icon, colorByGroupBy, [a]))
            }

            return arr
        }, [] as Array<TimeCardContainer>)
    }

    groupByEmployee(items: Array<TimeCardContainer>, icon: string, colorByGroupBy: string): Array<TimeCardContainer> {
        return items.reduce((arr, a) => {
            const id = arr.findIndex((timesheet) =>
                timesheet.children
                    ? timesheet.children.findIndex((child) => child.employeeId === a.employeeId) >= 0
                    : false
            )

            if (id >= 0) {
                notUndefined(arr[id].children).push(a)
            } else {
                arr.push(
                    new TimeCardContainer(
                        a.employeeFullName ? a.employeeFullName : this.getFallbackEmployeeName(a.employeeId),
                        icon,
                        colorByGroupBy,
                        [a]
                    )
                )
            }

            return arr
        }, [] as Array<TimeCardContainer>)
    }

    groupByTimesheetProjectClient(
        items: Array<TimeCardContainer>,
        icon: string,
        colorByGroupBy: string
    ): Array<TimeCardContainer> {
        return items.reduce((arr, a) => {
            const id = arr.findIndex((timesheet) =>
                timesheet.children
                    ? timesheet.children.findIndex((child) => child.timesheetProjectId === a.timesheetProjectId) >= 0
                    : false
            )

            if (id >= 0) {
                notUndefined(arr[id].children).push(a)
            } else {
                arr.push(
                    new TimeCardContainer(`${a.clientName} (${a.timesheetProjectName})`, icon, colorByGroupBy, [a])
                )
            }

            return arr
        }, [] as Array<TimeCardContainer>)
    }

    groupByTimesheetProject(
        items: Array<TimeCardContainer>,
        icon: string,
        colorByGroupBy: string
    ): Array<TimeCardContainer> {
        return items.reduce((arr, a) => {
            const id = arr.findIndex((timesheet) =>
                timesheet.children
                    ? timesheet.children.findIndex((child) => child.timesheetProjectId === a.timesheetProjectId) >= 0
                    : false
            )

            if (id >= 0) {
                notUndefined(arr[id].children).push(a)
            } else {
                arr.push(new TimeCardContainer(`${a.timesheetProjectName}`, icon, colorByGroupBy, [a]))
            }

            return arr
        }, [] as Array<TimeCardContainer>)
    }

    groupByClient(items: Array<TimeCardContainer>, icon: string, colorByGroupBy: string): Array<TimeCardContainer> {
        return items.reduce((arr, a) => {
            const id = arr.findIndex((timesheet) =>
                timesheet.children
                    ? timesheet.children.findIndex((child) => child.clientName === a.clientName) >= 0
                    : false
            )

            if (id >= 0) {
                notUndefined(arr[id].children).push(a)
            } else {
                arr.push(new TimeCardContainer(`${a.clientName}`, icon, colorByGroupBy, [a]))
            }

            return arr
        }, [] as Array<TimeCardContainer>)
    }

    private getFallbackEmployeeName(employeeId: number): UUID | string {
        const employee = notUndefined(this.allEmployees).find((e) => e.id === employeeId)

        return employee ? employee.fullName : '<undefined>'
    }
}
